import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Car, MapPin, ShoppingBag, Globe, Shield, Users, Navigation,
  Radio, Music, Ticket, MessageSquare, Heart, Zap, Fuel, Compass,
  Download, Bell, Eye, StickyNote, Star, AlertTriangle,
  Clock, Map, Headphones, Megaphone, X
} from 'lucide-react';

const IMAGES = {
  skating: 'https://images.unsplash.com/photo-1475859880971-f389e8a1bda8?w=800&h=500&fit=crop',
  navigation: 'https://images.unsplash.com/photo-1759256243611-502772ac391b?w=800&h=500&fit=crop',
  community: 'https://images.unsplash.com/photo-1752761400309-83ce512dce78?w=800&h=500&fit=crop',
  travel: 'https://images.unsplash.com/photo-1692023391280-b526919883d1?w=800&h=500&fit=crop',
  marketplace: 'https://images.unsplash.com/photo-1682292508562-d93da8eaf271?w=800&h=500&fit=crop',
  crisis: 'https://images.unsplash.com/photo-1700574005887-47134ebde2ba?w=800&h=500&fit=crop',
};

const FeatureCard = ({ icon, title, desc }) => (
  <div className="flex gap-3 p-3 rounded-xl bg-zinc-900/60 border border-zinc-800/50">
    <div className="shrink-0 w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400">{icon}</div>
    <div>
      <h4 className="text-sm font-bold text-white mb-0.5">{title}</h4>
      <p className="text-xs text-zinc-400 leading-relaxed">{desc}</p>
    </div>
  </div>
);

const SectionBlock = ({ image, icon, title, badge, description, features, children }) => (
  <div className="rounded-2xl overflow-hidden border border-zinc-800/60 bg-zinc-900/30">
    <img src={image} alt={title} className="w-full h-40 sm:h-52 object-cover" loading="lazy" />
    <div className="p-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h2 className="text-lg font-black text-white">{title}</h2>
        {badge && (
          <span className="ml-auto text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
            {badge}
          </span>
        )}
      </div>
      {description}
      {children}
      {features && (
        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {features.map((f, i) => <FeatureCard key={i} {...f} />)}
        </div>
      )}
    </div>
  </div>
);

const ShowcasePage = ({ onClose: externalClose }) => {
  const navigate = useNavigate();

  // Lock body scroll while overlay is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleClose = () => {
    if (externalClose) externalClose();
    else navigate('/');
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 sm:p-8" data-testid="showcase-overlay">
      {/* Blurred backdrop — site visible behind */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[3px]" onClick={handleClose} />

      {/* Scrollable content panel — compact */}
      <div
        className="relative w-full max-w-md mx-auto bg-[#0d0d0d]/95 rounded-2xl overflow-hidden animate-in slide-in-from-bottom duration-300 border border-zinc-800/50"
        style={{ maxHeight: '75vh' }}
        data-testid="showcase-panel"
      >
        {/* Close button — z-50 so it sits above the inner hero content
            (which uses its own relative z-10 and was intercepting taps) */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/20 hover:bg-white/10 active:scale-95 transition-all text-sm font-semibold text-white shadow-lg"
          data-testid="showcase-close-btn"
          aria-label="Close showcase"
        >
          <X className="w-4 h-4" />
          <span>Skip</span>
        </button>

        {/* Scrollable inner content */}
        <div className="overflow-y-auto showcase-scroll" style={{ maxHeight: '75vh' }}>
          <style>{`
            .showcase-scroll::-webkit-scrollbar { width: 6px; }
            .showcase-scroll::-webkit-scrollbar-track { background: transparent; }
            .showcase-scroll::-webkit-scrollbar-thumb { background: rgba(200,200,200,0.5); border-radius: 3px; }
            .showcase-scroll::-webkit-scrollbar-thumb:hover { background: rgba(200,200,200,0.7); }
            .showcase-scroll { scrollbar-color: rgba(200,200,200,0.5) transparent; scrollbar-width: thin; }
          `}</style>
          {/* Hero */}
          <div className="relative px-5 pt-8 pb-6 text-center">
            <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 via-transparent to-transparent" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold mb-4">
                <Zap className="w-3 h-3" /> Platform Showcase
              </div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">RollM8s</span>
              </h1>
              <p className="text-base text-zinc-300 mb-1 font-semibold">The UK Skate, Wheels & Boards Community Platform</p>
              <p className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
                One app for the entire Skate, Wheels & Boards community — roller, inline, skateboard, longboard, BMX, scooter and snow sports. Events, rides, marketplace, crisis mapping,
                global trips, offline navigation, and real community features.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-4 text-[10px] font-bold text-zinc-600 uppercase tracking-wider">
                <span>Events</span><span className="text-cyan-500/30">|</span>
                <span>Rides</span><span className="text-cyan-500/30">|</span>
                <span>Listings</span><span className="text-cyan-500/30">|</span>
                <span>Crisis Maps</span><span className="text-cyan-500/30">|</span>
                <span>World Trips</span><span className="text-cyan-500/30">|</span>
                <span>Community</span>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="px-4 pb-8 space-y-4">
            {/* Events & Ticketing */}
            <SectionBlock
              image={IMAGES.skating}
              icon={<Calendar className="w-5 h-5 text-cyan-400" />}
              title="Events & Ticketing"
              description={
                <p className="text-sm text-zinc-400 leading-relaxed mb-2">
                  Browse skate, board and wheel sport events across the UK. Purchase tickets directly with secure Stripe payments.
                  Registered members can create and list their own events with full organiser dashboards — manage tiers, scan QR codes, and track sales.
                </p>
              }
              features={[
                { icon: <Ticket className="w-4 h-4" />, title: "Ticket Wallet", desc: "All purchased tickets in one place with QR codes" },
                { icon: <Clock className="w-4 h-4" />, title: "Waitlists", desc: "Join waitlists for sold-out events" },
                { icon: <Star className="w-4 h-4" />, title: "Multi-Tier Pricing", desc: "Early bird, VIP, and standard tiers" },
                { icon: <Bell className="w-4 h-4" />, title: "Push Notifications", desc: "Alerts for events and bookings" },
              ]}
            >
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="rounded-lg overflow-hidden border border-zinc-700/40">
                  <img src="https://images.unsplash.com/photo-1600147131759-880e94a6185f?w=400&h=250&fit=crop" alt="Scanning QR code ticket" className="w-full h-24 sm:h-32 object-cover" loading="lazy" />
                  <p className="text-[10px] text-zinc-500 text-center py-1 bg-zinc-900/80">QR Ticket Scanning</p>
                </div>
                <div className="rounded-lg overflow-hidden border border-zinc-700/40">
                  <img src="https://images.unsplash.com/photo-1701118839531-8798ec375827?w=400&h=250&fit=crop" alt="Digital QR code on phone" className="w-full h-24 sm:h-32 object-cover" loading="lazy" />
                  <p className="text-[10px] text-zinc-500 text-center py-1 bg-zinc-900/80">Digital QR Tickets</p>
                </div>
              </div>
            </SectionBlock>

            {/* Ride-Share */}
            <SectionBlock
              image={IMAGES.navigation}
              icon={<Car className="w-5 h-5 text-cyan-400" />}
              title="Event-Linked Ride-Share"
              description={
                <p className="text-sm text-zinc-400 leading-relaxed mb-2">
                  Cost-sharing for community members heading to the same events. Drivers offer seats,
                  set pickup points, and passengers share petrol cost fairly.
                </p>
              }
              features={[
                { icon: <Navigation className="w-4 h-4" />, title: "Full Sat-Nav", desc: "Turn-by-turn voice navigation for drivers" },
                { icon: <Eye className="w-4 h-4" />, title: "Live Tracking", desc: "Passengers see driver location — Uber-style" },
                { icon: <Fuel className="w-4 h-4" />, title: "Fuel Splitter", desc: "Automatic cost splitting based on distance" },
                { icon: <MapPin className="w-4 h-4" />, title: "Flexible Pickups", desc: "Multiple pickup points per ride" },
              ]}
            />

            {/* Blackout Zone */}
            <SectionBlock
              image={IMAGES.crisis}
              icon={<Shield className="w-5 h-5 text-red-400" />}
              title="Blackout Zone"
              badge="Members only"
              description={
                <div className="space-y-1 mb-2">
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    Over <strong className="text-white">72,000 UK crisis resources</strong> mapped — food banks, water, shelters,
                    bunkers, medical, charging stations and more.
                  </p>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    Full turn-by-turn sat-nav with voice guidance — <strong className="text-white">even with no internet or mobile signal</strong>.
                  </p>
                </div>
              }
              features={[
                { icon: <Map className="w-4 h-4" />, title: "72K+ Resource Pins", desc: "Food, shelters, water, medical and more" },
                { icon: <Download className="w-4 h-4" />, title: "Offline Road Network", desc: "Full UK road graph — navigate without internet" },
                { icon: <Navigation className="w-4 h-4" />, title: "Offline Turn-by-Turn", desc: "A* routing runs entirely in your browser" },
                { icon: <Headphones className="w-4 h-4" />, title: "Voice Navigation", desc: "Spoken directions — online and offline" },
                { icon: <AlertTriangle className="w-4 h-4" />, title: "Incidents", desc: "Report road closures and flooding" },
                { icon: <Compass className="w-4 h-4" />, title: "Heatmap", desc: "See resource density across the UK" },
              ]}
            />

            {/* World Trips */}
            <SectionBlock
              image={IMAGES.travel}
              icon={<Globe className="w-5 h-5 text-amber-400" />}
              title={<>World Trips <img src="https://flagcdn.com/w40/fr.png" alt="France" className="inline h-4 w-auto -mt-0.5" /></>}
              badge="Members only"
              description={
                <p className="text-sm text-zinc-400 leading-relaxed mb-2">
                  International skate, wheel & board events from around the world. Mark yourself as "I'm Going"
                  and connect with skaters planning the same trips.
                </p>
              }
              features={[
                { icon: <Globe className="w-4 h-4" />, title: "Global Calendar", desc: "Events from 14+ countries" },
                { icon: <Map className="w-4 h-4" />, title: "Map View", desc: "Events on a world map with flag markers" },
                { icon: <Users className="w-4 h-4" />, title: "I'm Going", desc: "See who's travelling to each event" },
              ]}
            />

            {/* Community Listings */}
            <SectionBlock
              image={IMAGES.marketplace}
              icon={<ShoppingBag className="w-5 h-5 text-green-400" />}
              title="Community Listings"
              description={
                <p className="text-sm text-zinc-400 leading-relaxed mb-2">
                  Buy, sell, swap, and offer skate, board, BMX, scooter and snow gear within the community.
                  List items with photos, browse by category, and connect directly.
                </p>
              }
              features={[
                { icon: <ShoppingBag className="w-4 h-4" />, title: "Buy, Sell, Swap", desc: "List gear, swap items, or offer services" },
                { icon: <MessageSquare className="w-4 h-4" />, title: "Direct Messaging", desc: "Chat with sellers and buyers in-app" },
              ]}
            />

            {/* Community */}
            <SectionBlock
              image={IMAGES.community}
              icon={<Users className="w-5 h-5 text-purple-400" />}
              title="Community Features"
              description={
                <p className="text-sm text-zinc-400 leading-relaxed mb-2">
                  Not just tools — a real community. Anonymous peer support, music sharing,
                  fun competitions, and dedicated spaces for different vibes.
                </p>
              }
              features={[
                { icon: <Heart className="w-4 h-4" />, title: "Safe Space", desc: "Anonymous peer support for mental health" },
                { icon: <Megaphone className="w-4 h-4" />, title: "Unfiltered", desc: "No-judgment zone to vent and share" },
                { icon: <Star className="w-4 h-4" />, title: "Floor Tax", desc: "Post your skating stacks for laughs" },
                { icon: <StickyNote className="w-4 h-4" />, title: "Post-it Notes", desc: "Quick shoutouts to other skaters" },
                { icon: <Users className="w-4 h-4" />, title: "Corners", desc: "DJ and Organiser profile pages" },
                { icon: <Music className="w-4 h-4" />, title: "Music Player", desc: "Floating player with skating playlists" },
              ]}
            />

            {/* And More */}
            <div className="text-center pt-2 pb-1">
              <h2 className="text-lg font-black text-white mb-0.5">And More...</h2>
              <p className="text-xs text-zinc-500">Every detail built for the Skate, Wheels & Boards community</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <FeatureCard icon={<Radio className="w-4 h-4" />} title="SOS Flasher" desc="Emergency torch/screen flash in SOS Morse code" />
              <FeatureCard icon={<Compass className="w-4 h-4" />} title="Each One Help One" desc="Post what you need or what you can offer" />
              <FeatureCard icon={<Fuel className="w-4 h-4" />} title="Live Fuel Prices" desc="Real-time UK fuel prices on the map" />
              <FeatureCard icon={<Navigation className="w-4 h-4" />} title="Waypoints" desc="Drop pins during navigation to add stops" />
            </div>

            {/* Footer */}
            <div className="pt-6 pb-2 text-center">
              <p className="font-logo text-2xl tracking-wide mb-1">
                <span className="text-white">ROLL</span>
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">M8S</span>
              </p>
              <p className="text-[10px] text-zinc-600 uppercase tracking-widest font-bold">The UK Skate, Wheels & Boards Community</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShowcasePage;
